import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18n';

/** 401 guard — clears admin token and kicks to /admin/login with session banner */
export const useGuard401 = () => {
  const nav = useNavigate();
  return useCallback(
    err => {
      if (err?.status === 401) {
        localStorage.removeItem('adm-token');
        localStorage.removeItem('adm-user');
        nav('/admin/login?reason=session', { replace: true });
        return true;
      }
      return false;
    },
    [nav],
  );
};

export function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function PaginationBar({ page, pageSize, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
  if (!total || total <= pageSize) return null;

  return (
    <nav className="adm-pagination" aria-label="Pagination">
      <p className="adm-pagination-info">
        Page <strong>{page}</strong> of <strong>{totalPages}</strong>
        <span className="adm-pagination-dot" aria-hidden="true">·</span>
        <span className="adm-pagination-total">{total} total</span>
      </p>
      <div className="adm-pagination-controls">
        <button
          type="button"
          className="adm-pagination-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Previous
        </button>
        <button
          type="button"
          className="adm-pagination-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
        </button>
      </div>
    </nav>
  );
}

export function ConfirmationDialog({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }) {
  const { t } = useI18n();
  if (!isOpen) return null;

  return (
    <div className="confirmation-overlay">
      <div className="confirmation-dialog">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="confirmation-actions">
          <button onClick={onClose} className="btn-secondary">
            {cancelText || t('cancel')}
          </button>
          <button onClick={onConfirm} className="btn-danger">
            {confirmText || t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
